import React, { useState } from "react";
import { useTheme, ListItem, Card, Box, Stack, Divider, Typography, Tooltip, Link, IconButton, Chip,
         DialogTitle, DialogContent, DialogActions, Button, List, Paper } from "@mui/material";
import { ThumbUp, Comment, Share, Visibility, Group, TrendingUp, ExpandMore, Translate, 
         KeyboardDoubleArrowRight, Search, MenuOpen, Videocam } from "@mui/icons-material";
import APIRequest from "system/APIRequest";
import useAuth, { getUserLanguage } from "system/APIAuth";
import { permissionTest } from "system/InfegyUser";
import { useTranslation } from 'react-i18next';
import { useSnackbar } from "notistack";
import ResponsiveDialog from "components/dialogs/ResponsiveDialog";
import AbbreviateNumber from "components/formatting/AbbreviateNumber";
import TitleCase from "components/formatting/TitleCase";
import ISODateTime from "components/formatting/ISODateTime";
import SanitizedHTML from "components/formatting/SanitizedHTML";
import SocialIcon from "components/widgets/utility/SocialIcon";
import type { Post, Label } from "./PostListUtils";

interface PostListItemProps {
  index: number; 
  post: Post;
  labels: Label | undefined;
}

export default function PostListItem({ index, post, labels }: PostListItemProps): React.JSX.Element {
  const auth = useAuth();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { i18n } = useTranslation();

  const [detailViewActive, setDetailViewActive] = useState<boolean>(false);
  const [expandedViewActive, setExpandedViewActive] = useState<boolean>(false);
  const [queryMatchDialogActive, setQueryMatchDialogActive] = useState<boolean>(false);
  const [translatedPost, setTranslatedPost] = useState<{ title?:string; body:string } | null>(null);
  const [isTranslating, setIsTranslating] = useState<boolean>(false);

  const userLanguage = getUserLanguage(auth.user);
  const longPostLength = 750;
  const isLongPost = post?.body?.length > longPostLength;
  const isLongPostExpanded = isLongPost && expandedViewActive;
  const hasQueryMatches = post._match_info && Object.keys(post._match_info).length > 0;
  const link = post.link ? (post.link.includes("://") ? post.link : `https://${post.link}`) : "";

  const postHighlightingStyle = {
      "& i": {
      display: "inline-block",
      position: "relative",
      fontStyle: "normal",
      zIndex: 1979,
    },
    // Raising the z-index of the hovered element to ensure its tooltip appears above siblings
    "& i:hover": {
      zIndex: 9999,
    },
    // :hover bubbles to ancestors, so a nested match's own tooltip is suppressed in favor of its parent
    "& i i:hover::after": {
      display: "none",
    },
    "& i::before": {
      content: '" "',
      borderRadius: "6px",
      border: `1px solid ${theme.colors.primary.dark}`,
      backgroundColor: theme.colors.primary.main,
      opacity: "20%",
      display: "block",
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      zIndex: 1978
    },
    "& i:hover::after": {
      content: `"${i18n.t("widget-types.post-list.post.query-match")}"`,
      borderRadius: "6px",
      border: `1px solid ${theme.palette.divider}`,
      fontSize: "1rem",
      fontWeight: "normal",
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.background.paper,
      boxShadow: `0 0 4px ${theme.palette.divider}`,
      display: "block",
      position: "absolute",
      top: "100%",
      left: "-100%",
      textAlign: "center",
      minWidth: "140px",
      minHeight: "28px",
    }
  }
  
  // Send post body to API for translation; update the state with the translated text.
  const handleTranslate = async () => {
    setIsTranslating(true);
    const translationRequestInfo: { body: string; title?: string } = {
      body: post.body
    };

    if (post?.title) {
      translationRequestInfo.title = post.title;
    }

    try {
      const response = await APIRequest("translate", {}, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(translationRequestInfo),
      });
      const data = await response; 
      setTranslatedPost({
        title: data?.title || post?.title,
        body: data?.body
      });
      setIsTranslating(false);
    } catch (error) {
      enqueueSnackbar(i18n.t("widget-types.post-list.translation.error", {errorMessage: error}), { variant: "error" });
      setIsTranslating(false);
    }
  };

  // Toggle the detailed view and expanded view for long posts when the post is clicked
  const handlePostClick = () => {
    setDetailViewActive(!detailViewActive);
    if (isLongPost) {
      setExpandedViewActive(!expandedViewActive);
    }
  };

  // Utility function to calculate relative time from published date
  const relativeTime = (published:string): string => {
    const publishedDate = new Date(published);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - publishedDate.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return `${diffInSeconds}s`;
    } else if (diffInSeconds < 3600) {
      return `${Math.floor(diffInSeconds / 60)}m`;
    } else if (diffInSeconds < 86400) {
      return `${Math.floor(diffInSeconds / 3600)}h`;
    } else if (diffInSeconds < 604800) {
      return `${Math.floor(diffInSeconds / 86400)}d`;
    } else if (diffInSeconds < 2419200) {
      return `${Math.floor(diffInSeconds / 604800)}w`;
    } else if (diffInSeconds < 29030400) {
      return `${Math.floor(diffInSeconds / 2419200)}m`;
    } else {
      return `${Math.floor(diffInSeconds / 29030400)}y`;
    }
  };

  // Utility function to normalize author names for handling platform variations
  // Can be used with other platforms to tailor author name, depending on format
  const normalizeAuthor = (author:string | null): string => {
    if (!author) return "";

    // Remove any parenthesis and their contents from author string, removing redundant information and condenses string for rendering
    const authorWithoutParenthesis = author.replace(/\s*\(.*?\)\s*/g, "").trim();

    // Instagram: If author contains " and ", split it, return the first author with remaining others
    // Author data: "@user1 and @user2 and @user3..."
    if (post.site === "instagram.com") {
      if (authorWithoutParenthesis.includes(" and ")) {
        const authorParts = authorWithoutParenthesis.split(" and ");
        const firstAuthor = authorParts[0].trim();
        const remainingAuthorsCount = authorParts.length - 1;
        // Transform: "@user1 and X others"
        return `${firstAuthor} ${i18n.t("and")} ${remainingAuthorsCount} ${i18n.t("others")}`;
      } else {
        return authorWithoutParenthesis;
      }
    }

    return authorWithoutParenthesis;
  };

  // Render the author and relative time in a single line, with tooltip for full details
  const renderAuthor = () => {
    const sourceName = normalizeAuthor(post.author) || post.site;
    const relativeTimeString = relativeTime(post.published);

    const tooltip = (
      <Box>
        <Typography sx={{ fontWeight: "bold" }}>{sourceName}</Typography>
          {post.handle && (
            <Typography sx={{ fontStyle: "italic", color: theme.colors.namedColors.chart1 }}>@{post.handle}</Typography>
          )}
        <ISODateTime isoString={post.published} hideTime={false} hideTooltip={true} />
      </Box>
    );

    return (
      <Tooltip title={tooltip} followCursor arrow>
        <Box component="span">{`${sourceName} • ${relativeTimeString}`}</Box>
      </Tooltip>
    );
  };

  const renderTitle = () => {
     let title = translatedPost?.title ? translatedPost.title : post.title;
      if (!title) { title = post.link }

      // Find if the post link corresponds to a Reddit comment by checking the URL pattern
      const isRedditComment = /\/comments\/[^/?#]+\/[^/?#]+\/[^/?#]+/.test(post.link);
      const isInstagramReel = /\/reel\//.test(post.link);

      return (
        <Box sx={{ ...postHighlightingStyle, display: "inline-flex", alignItems: "center" }}>
          {isRedditComment && (
            <Tooltip title={i18n.t("widget-types.post-list.post.comment")} followCursor arrow>
              <Comment sx={{ fontSize: "1rem", mr: 1, color: theme.colors.namedColors.reddit }} />
            </Tooltip>
          )}
          {isInstagramReel && (
            <Tooltip title={i18n.t("widget-types.post-list.post.reel")} followCursor arrow>
              <Videocam sx={{ fontSize: "1rem", mr: 1, color: theme.colors.namedColors.instagram }} />
            </Tooltip>
          )}
          <SanitizedHTML
            html={title}
            forbidImages={true}
            forbidFormatting={false}
          />
        </Box>
      )
  };

  // Render a row of metadata with label and values, using chips for each value.
  // If nested is true, split values by "|" and render them as arrows.
  const getLabel = (label: string, value: string) => labels?.[label]?.[value] ?? value;
  const sortedValues = (values: string[]) => [...values].sort((first, second) => first.localeCompare(second));
  const renderNestedValues = (value: string ) => {
    const valuesArray = value.split("|");
    return valuesArray.map((val, index) => (
      <Box key={index} sx={{ display: "inline-flex", alignItems: "center" }}>
        <TitleCase text={val} />
        {index < valuesArray.length - 1 && (
          <Box sx={{ display: "inline-flex", alignItems: "center", width: "16px", mx: 0.5 }}>
            <KeyboardDoubleArrowRight sx={{ fontSize: "1rem", }} />
          </Box>
        )}
      </Box>
    ));
  };

  // Clean up  any remaining HTML tags
  const renderPost = (post: Post) => {
    if (!post.body) return "";
    const body = translatedPost?.body ?? post.body;
    const displayedBody = isLongPost && !isLongPostExpanded ? `${body.slice(0, longPostLength)}...` : body;

    return (
      <Box sx={postHighlightingStyle}>
        <SanitizedHTML
          html={displayedBody}
          forbidImages={true}
          forbidFormatting={false}
        />
      </Box>
    );
  };

  // Render the post description with a label, highlighted, and sanitized HTML content.
  const renderDescription = (post: Post) => {
    if (!post.description) return null;
    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", mb: 1 }}>
        <Typography variant="subtitle1" sx={{ mr: 1 }}>{i18n.t("description")}:</Typography>
        <Box sx={postHighlightingStyle}>
          <SanitizedHTML
            html={post.description}
            forbidImages={true}
            forbidFormatting={false}
          />
        </Box>
      </Box>
    )
  }

  // Render a row of metadata with label and values, using chips for each value.
  const renderMetadataRow = (label: string, values: string | string[], nested = false) => {
    const valuesArray = Array.isArray(values) ? values : values ? [values] : [];
    if (!valuesArray.length) return null;

    const displayValues = nested ? sortedValues(valuesArray) : valuesArray;

    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", mb: 1 }}>
        <Typography variant="subtitle1" sx={{ mr: 1 }}>{label}:</Typography>
        {displayValues.map((value, valueIndex) => (
          <Chip
            key={`${value}-${valueIndex}`}
            label={nested ? renderNestedValues(value) : <TitleCase text={getLabel(label.toLowerCase(), value)} />}
            size="small"
            sx={{ mr: 0.5, mb: 0.5, borderRadius: "6px" }}
          />
        ))}
      </Box>
    );
  };

  // Render post numerical data with an icon, tooltip, and value.
  const renderStatistic = (Icon: React.ElementType, tooltip: string, value: number | undefined, color: string) => {
    if (value === undefined || value === null) return null;

    return (
      <Tooltip title={tooltip} followCursor arrow>
        <Typography sx={{ display: "flex", alignItems: "center", mr: 1 }}>
          <Icon sx={{ height: "14px", width: "14px", color }} />
          <Typography variant="caption" sx={{ ml: .5 }}>
            <AbbreviateNumber 
              value={value} 
              hideTooltip={true} 
              options={{ maximumSignificantDigits: 3 }} 
              sx={{ fontSize: ".8rem", fontWeight: "bold" }} />
          </Typography>
        </Typography>
      </Tooltip>
    );
  };

  // Render the influence bar with color coding based on thresholds, and a tooltip showing the influence value.
  const renderInfluenceBar = (influence: number) => {
    if (influence <= 1) return null;

    const influenceThresholds = [79, 120, 184];

    const influenceColor = (influence: number) =>
      influence <= influenceThresholds[0] ? theme.colors.namedColors.passionLighter :
      influence <= influenceThresholds[1] ? theme.colors.namedColors.passionLight :
      influence <= influenceThresholds[2] ? theme.colors.namedColors.passion :
      theme.colors.namedColors.passionDark;

    return (
      <Tooltip title={`${i18n.t("widget-types.post-list.post.influence")}: ${influence}/255`} followCursor arrow>
        <Box sx={{ display: "flex", justifyItems: "flex-end", mr: 1 }}>
          <Box sx={{
            display: "inline-block",
            mr: 1,
            width: "100px",
            height: "10px",
            borderRadius: "10px",
            backgroundColor: theme.colors.alpha.black[10],
            border: `1px solid ${theme.colors.alpha.black[5]}`,
            position: "relative" }}>
            <Box sx={{
              position: "absolute",
              top: 0,
              left: 0,
              height: "100%",
              width: `${(influence / 255) * 100}%`,
              borderRadius: "10px",
              backgroundColor: influenceColor(influence) }}></Box>
            {influenceThresholds.map((threshold) => (
              <Box key={threshold} sx={{ position: "absolute", top: 0, left: `${(threshold / 255) * 100}%`, height: "100%", width: "1px", backgroundColor: theme.colors.alpha.white[50] }}></Box>
            ))}
          </Box>
        </Box>
      </Tooltip>
    );
  };

  // Render the sentiment indicator with a colored box and tooltip showing the sentiment label.
  const renderSentiment = (sentiment: string) => {
    if (!sentiment) return null;

    const sentimentLabels: Record<string, string> = { n: i18n.t("negative"), p: i18n.t("positive") };
    const sentimentColors: Record<string, string> = { n: theme.colors.namedColors.chart9, p: theme.colors.namedColors.chart8 };

    return (
      <Tooltip title={`${i18n.t("sentiment")}: ${sentimentLabels[sentiment] ?? i18n.t("neutral")}`} followCursor arrow>
        <Box sx={{
          width: "16px",
          height: "16px",
          borderRadius: "4px",
          border: `1px solid ${theme.colors.alpha.black[5]}`,
          backgroundColor: sentimentColors[sentiment] ?? theme.palette.grey[300],
          mr: 1
        }} />
      </Tooltip>
    );
  };

  return (
    <ListItem sx={{ display: "flex", zIndex: index }}>
      <Card sx={{ flex: "1", py: 1, px: .5, border: `1px solid ${theme.sidebar.dividerBg}`}}>
        <Stack direction="column" spacing={1} sx={{ width: "100%", height: "100%" }}>

          {/* Row 1: Icon | Title | Author */}
          <Stack direction="row" sx={{ width: "100%", height: "25%", alignItems: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", width: "100%", px: .5 }}>
              <Box sx={{ width: "24px", minWidth: "24px", height: "24px", textAlign: "center", mr: 1 }}>
                <Tooltip title={<TitleCase text={post.site} />} followCursor arrow>
                  <Box>
                    <SocialIcon site={post.site} />
                  </Box>
                </Tooltip>
              </Box>
              <Stack direction="row" sx={{ width: "100%", minWidth: 0 }}>
                <Box sx={{ width: "60%", textAlign: "left", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", fontSize: "0.8rem", fontWeight: "bold"}}>
                  <Link href={link} target="_blank" rel="noreferrer">
                    {renderTitle()}
                  </Link>
                </Box>
                <Box sx={{ width: "40%", textAlign: "right", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", fontSize: "0.8rem" }}>
                  {renderAuthor()}
                </Box>
              </Stack>
            </Box>
          </Stack>
          <Divider sx={{ width: "100%", mt: 1 }} />

          {/* Row 2: Tab Buttons | Post Body + Details */}
          <Stack direction="row" spacing={1} sx={{ width: "100%", height: "50%" }}>
            <Stack sx={{ width: "32px", minWidth: "32px", justifyContent: "flex-start" }}>
              {isLongPost && 
                <Box>
                  <Tooltip title={expandedViewActive ? i18n.t("collapse") : i18n.t("expand")} followCursor arrow>
                    <IconButton color={expandedViewActive ? "primary" : "default"} onClick={() => setExpandedViewActive(!expandedViewActive)}>
                      <ExpandMore sx={{
                        fontSize: "1.25rem",
                        transform: expandedViewActive ? "rotate(180deg)" : "rotate(0deg)",
                        transition: theme.transitions.create("transform", { duration: theme.transitions.duration.short }) }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              }
              <Box>
                <Tooltip title={i18n.t("details")} followCursor arrow>
                  <IconButton color={detailViewActive ? "primary" : "default"} onClick={() => setDetailViewActive(!detailViewActive)}>
                    <MenuOpen sx={{fontSize: "1.25rem" }}/>
                  </IconButton>
                </Tooltip>
              </Box>
              {hasQueryMatches &&
                <Box>
                  <Tooltip title={i18n.t("widget-types.post-list.post.query-matches")} followCursor arrow>
                    <IconButton onClick={() => setQueryMatchDialogActive(!queryMatchDialogActive)}>
                      <Search sx={{fontSize: "1.25rem" }}/>
                    </IconButton>
                  </Tooltip>
                </Box>
              }
              {permissionTest("translate_text") && userLanguage && post.language && userLanguage !== post.language && 
                <Box>
                  <Tooltip title={translatedPost ? i18n.t("widget-types.post-list.translation.translated") : i18n.t("widget-types.post-list.translation.translate")} followCursor arrow>
                    <IconButton color={isTranslating ? "primary" : "default"} onClick={handleTranslate} disabled={!!translatedPost} loading={isTranslating}>
                      <Translate sx={{ fontSize: "1.25rem" }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              }
            </Stack>
            <Box sx={{ width: "100%", whiteSpace: "wrap", cursor: "pointer" }} onClick={handlePostClick}>
              <Box sx={{ width: "100%", wordBreak: "break-word" }}>
                {renderPost(post)}
              </Box>
              {detailViewActive && 
                <Box sx={{ display: "flex", flexDirection: "column", wordBreak: "break-word", mt: 1 }}>
                  <Divider sx={{ mb: 1 }} />
                    {renderDescription(post)}
                    {renderMetadataRow(i18n.t("widget-types.post-list.post.country"), post.country)}
                    {renderMetadataRow(i18n.t("widget-types.post-list.post.language"), post.language)}
                    {renderMetadataRow(i18n.t("widget-types.post-list.post.entities"), post.entities)}
                    {renderMetadataRow(i18n.t("widget-types.post-list.post.emotions"), post.emotions, true)}
                    {renderMetadataRow(i18n.t("widget-types.post-list.post.themes"), post.themes, true)}
                    {renderMetadataRow(i18n.t("widget-types.post-list.post.interests"), post.source_interests, true)}
                    {renderMetadataRow(i18n.t("widget-types.post-list.post.subject"), post.taxonomies, true)}
                </Box>
              }
            </Box>
          </Stack>
          <Divider sx={{ width: "100%", mb: 1 }} />

          {/* Row 3: Statistics */}
          <Stack direction="row" sx={{ width: "100%", height: "25%" }}>
            <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", width: "100%", px: 1 }}>
              {renderStatistic(ThumbUp, i18n.t("widget-types.post-list.post.likes"), post.likes, theme.colors.namedColors.chart1Light)}
              {renderStatistic(Comment, i18n.t("widget-types.post-list.post.comments"), post.comments, theme.colors.namedColors.chart1)}
              {renderStatistic(Share, i18n.t("widget-types.post-list.post.shares"), post.shares, theme.colors.namedColors.chart1Dark)}
              {renderStatistic(TrendingUp, i18n.t("widget-types.post-list.post.engagements"), post.total_engagements, theme.colors.namedColors.chart1Light)}
              {renderStatistic(Visibility, i18n.t("widget-types.post-list.post.views"), post.views, theme.colors.namedColors.chart1)}
              {renderStatistic(Group, i18n.t("widget-types.post-list.post.followers"), post.followers, theme.colors.namedColors.chart1Dark)}
              {renderInfluenceBar(post.influence)}
              {renderSentiment(post.sentiment)}
            </Box>
          </Stack>
        </Stack>
      </Card>
      {queryMatchDialogActive && (
        <ResponsiveDialog
          open={queryMatchDialogActive}
          onClose={() => setQueryMatchDialogActive(false)}
          maxWidth="sm"
        >
          <DialogTitle>{i18n.t("widget-types.post-list.post.query-matches")}</DialogTitle>
          <DialogContent dividers sx={{ backgroundColor: theme.palette.background.default, p: 1 }}>
            <Paper sx={{ p: 0, mb: 1 }}>
              <List>
                {Object.entries(post._match_info ?? {}).map(([key, value], index) => (
                  <ListItem key={index}>
                    <Typography variant="body2" component="span">
                      <Typography variant="body2" component="span" sx={{ fontWeight: "bold" }}>{<TitleCase text={key} />}: </Typography> 
                      {Array.isArray(value) ? value.map(v => getLabel(key, v)).join(", ") : getLabel(key, String(value))}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            </Paper>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setQueryMatchDialogActive(false)}>{i18n.t("close")}</Button>
          </DialogActions>
        </ResponsiveDialog>
      )}
    </ListItem>
  );
}
